'use client'

import { useRef } from 'react'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { HorizontalScrollArea } from '@marketplace-web/shared/horizontal-scroll-area'
import { HomepageBanner } from '@marketplace-web/domain/banners'

import {
  userClickHomepageElement,
  userViewHomepageElement,
} from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'
import { BannerLayoutModel } from '@marketplace-web/domain/banners-data'

import HomepageBlockLayout from '../HomepageBlockLayout'

type Props = {
  elements: Array<BannerLayoutModel>
  name: string
  position: number
  homepageSessionId: string
  id: string
}

const BannersBlock = ({ elements, name, position, homepageSessionId, id }: Props) => {
  const { track } = useTracking()
  const seenBannerIndexes = useRef<Array<number>>([])

  const bannerType = elements.length === 1 ? 'wide' : 'narrow'

  const getTrackingData = (banner: BannerLayoutModel, index: number) => ({
    blockName: name,
    position: index + 1,
    contentSource: banner.contentSource,
    contentSourceLabel: '',
    contentSourceId: banner.id,
    homepageSessionId,
    screen: Screen.NewsFeed,
  })

  const handleItemClick = (banner: BannerLayoutModel, index: number) => () => {
    track(userClickHomepageElement(getTrackingData(banner, index)))
  }

  const handleItemView = (banner: BannerLayoutModel, index: number) => (inView: boolean) => {
    if (!inView) return
    if (seenBannerIndexes.current.includes(index)) return

    seenBannerIndexes.current.push(index)

    track(userViewHomepageElement(getTrackingData(banner, index)))
  }

  const renderBanners = () =>
    elements.map((banner, index) => (
      <HomepageBanner
        key={index}
        banner={banner}
        type={bannerType}
        onClick={handleItemClick(banner, index)}
        onView={handleItemView(banner, index)}
      />
    ))

  return (
    <HomepageBlockLayout
      name={name}
      position={position}
      homepageSessionId={homepageSessionId}
      id={id}
      body={
        <HorizontalScrollArea controlsScrollType={HorizontalScrollArea.ControlScrollType.Partial}>
          <div className={`${bannerType}-banners-layout-container`}>{renderBanners()}</div>
        </HorizontalScrollArea>
      }
    />
  )
}

export default BannersBlock
